// UserManagement page: allows Administrator to create and delete users.
import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, TextField, Button, Select, MenuItem, 
  Table, TableHead, TableRow, TableCell, TableBody, Box, 
  Card, CardContent, FormControl, InputLabel, Grid,
  TableContainer, Paper, IconButton, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Tooltip,
  Alert, Snackbar, CircularProgress
} from '@mui/material';
import apiClient from '../api/apiClient';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('VERIFIER');
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('');
  const [createError, setCreateError] = useState('');

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetError, setResetError] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/users');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setError(p.detail || p.title || 'Failed to load users.');
      } else {
        setError('Failed to load users.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!newUsername.trim() || !newUserPassword.trim() || !newUserConfirmPassword.trim()) {
        setCreateError('Username, Password, and Confirm Password are required.');
        return;
    }
    if (newUserPassword !== newUserConfirmPassword) {
        setCreateError('Passwords do not match.');
        return;
    }
    try {
      await apiClient.post('/users', { 
          username: newUsername, 
          password: newUserPassword, 
          role: newUserRole 
      });
      setSnackbarMessage('User created successfully!');
      setSnackbarOpen(true);
      setNewUsername('');
      setNewUserPassword('');
      setNewUserConfirmPassword('');
      setNewUserRole('VERIFIER');
      fetchUsers();
    } catch (err) {
        console.error("Failed to create user:", err);
        if (err.response && err.response.data) {
          const p = err.response.data;
          setCreateError(p.detail || p.title || 'Failed to create user.');
        } else {
          setCreateError('Failed to create user.');
        }
    }
  };
  
  const handleDelete = async (userId, username) => {
      if (window.confirm(`Are you sure you want to delete user ${username}?`)) {
          try {
            await apiClient.delete(`/users/${userId}`);
            setSnackbarMessage('User deleted successfully!');
            setSnackbarOpen(true);
            fetchUsers();
          } catch (err) {
              console.error("Failed to delete user:", err);
              if (err.response && err.response.data) {
                const p = err.response.data;
                setError(p.detail || p.title || 'Failed to delete user.');
              } else {
                setError('Failed to delete user.');
              }
          }
      }
  };

  const handleOpenResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetError('');
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setSelectedUser(null);
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      setResetError('New password and confirm password are required');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setResetError('Passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      setResetError('Password must be at least 4 characters long');
      return;
    }
    setResetError('');
    try {
      await apiClient.put(`/users/${selectedUser.id}/reset-password`, { new_password: newPassword });
      handleCloseResetDialog();
      setSnackbarMessage('Password reset successfully!');
      setSnackbarOpen(true);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      console.error("Failed to reset password:", err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setResetError(p.detail || p.title || 'Failed to reset password');
      } else {
        setResetError('Failed to reset password');
      }
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  return (
    <div className="page-container">
      <Container>
       {loading && <CircularProgress />}
       {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            User Management
          </Typography>
        </Box>
        
        <Card className="card form-container">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              Create New User
            </Typography>
            
            <Box component="form" onSubmit={handleCreate}>
              {createError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {createError}
                </Alert>
              )}
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="Enter username"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Password"
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="Enter password"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Confirm Password"
                    type="password"
                    value={newUserConfirmPassword}
                    onChange={(e) => setNewUserConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="Confirm password"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="role-select-label">Role</InputLabel>
                    <Select
                      labelId="role-select-label"
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                      label="Role"
                    >
                      <MenuItem value="ADMIN">Administrator</MenuItem>
                      <MenuItem value="SHIPOWNER">Shipowner</MenuItem>
                      <MenuItem value="SUPPLIER">Supplier</MenuItem>
                      <MenuItem value="VERIFIER">Verifier</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    fullWidth
                    startIcon={<PersonAddIcon />}
                    className="action-button"
                    sx={{ 
                      bgcolor: '#1565c0',
                      '&:hover': { bgcolor: '#0d47a1' },
                      height: '56px',
                    }}
                  >
                    CREATE USER
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
        
        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Registered Users
          </Typography>
        </Box>
        
        <TableContainer component={Paper} className="table-container">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow 
                  key={u.id}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>{u.username}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: u.role === 'Administrator' ? 'rgba(156, 39, 176, 0.1)' : 
                              u.role === 'Shipowner' ? 'rgba(33, 150, 243, 0.1)' :
                              u.role === 'Supplier' ? 'rgba(76, 175, 80, 0.1)' :
                              'rgba(255, 152, 0, 0.1)',
                      color: u.role === 'Administrator' ? '#9c27b0' : 
                             u.role === 'Shipowner' ? '#2196f3' :
                             u.role === 'Supplier' ? '#4caf50' :
                             '#ff9800',
                      fontWeight: 500
                    }}>
                      {u.role}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Reset Password">
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => handleOpenResetDialog(u)}
                        sx={{ mr: 1 }}
                      >
                        <LockResetIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleDelete(u.id, u.username)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Password reset dialog */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog}>
        <DialogTitle>
          Reset Password for {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            To reset the password for {selectedUser?.username}, please enter a new password below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="newPassword"
            label="New Password"
            type="password"
            fullWidth
            variant="standard"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!resetError}
          />
          <TextField
            margin="dense"
            id="confirmNewPassword"
            label="Confirm New Password"
            type="password"
            fullWidth
            variant="standard"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            error={!!resetError}
          />
          {resetError && <Alert severity="error" sx={{ mt: 2 }}>{resetError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleResetPassword} variant="contained" color="primary">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}